// API Client with Axios
import axios, { type AxiosInstance } from "axios";
import { clampPageSize } from "../utils/pagination";
import { safeGetItem, safeSetItem, safeRemoveItem } from "../utils/safe-storage";
import { emitSessionExpired, emitForceLogout } from "../utils/session-events";
import { singleFlight } from "../utils/single-flight";

const BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api/v1";

/** 对外暴露 API 前缀：SSE/WS 等无法走 axios 的场景（如通知推流）需要自行拼 URL */
export const API_BASE_URL = BASE_URL;
const client: AxiosInstance = axios.create({
  baseURL: BASE_URL, timeout: 15_000, headers: { "Content-Type": "application/json" },
  paramsSerializer: (params) => {
    const parts: string[] = [];
    for (const [key, val] of Object.entries(params)) {
      if (val === undefined || val === null) continue;
      if (Array.isArray(val)) {
        for (const v of val) parts.push(encodeURIComponent(key) + "=" + encodeURIComponent(String(v)));
      } else {
        parts.push(encodeURIComponent(key) + "=" + encodeURIComponent(String(val as string)));
      }
    }
    return parts.join("&");
  },
});

client.interceptors.request.use((cfg) => {
  const token = safeGetItem("accessToken");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  // 列表请求统一夹 page_size：服务端对「超上限」是**重置为 20**（不是夹到上限），
  // 前端写的 200/500 会静默变成 20 条 → 列表被截断且看不出问题（详见 utils/pagination.ts）
  if (cfg.params) cfg.params = clampPageSize(cfg.params as Record<string, unknown>);
  return cfg;
});

/**
 * 单飞静默刷新：`/auth/refresh` 是**轮换型**刷新（成功后旧 refresh token 作废），
 * 因此启动流程（AuthContext）与 401 拦截器必须共用同一次请求 —— 各打一次会让其中一条必然 401
 *（2026-09-25 线上 `401 POST /api/v1/auth/refresh` 的成因）。返回并落库新的 accessToken。
 */
export const silentRefresh = singleFlight(async (): Promise<string> => {
  const resp = await apiRefresh();
  const token = resp.data?.data?.accessToken || resp.data?.accessToken;
  if (!token) throw new Error("刷新响应缺少 accessToken");
  safeSetItem("accessToken", token);
  return token;
});

// 401 响应拦截器：先尝试静默刷新；刷新失败则通知表单缓存数据后跳转登录
client.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    if (error.response?.status === 401) {
      const isLoginRequest = error.config?.url?.includes("/auth/login");
      const isRefreshRequest = error.config?.url?.includes("/auth/refresh");
      const isRetried = error.config?._retry;

      // 非登录/刷新请求且未重试过 → 尝试静默刷新
      if (!isLoginRequest && !isRefreshRequest && !isRetried) {
        try {
          const newToken = await silentRefresh();
          error.config._retry = true;
          error.config.headers = error.config.headers || {};
          error.config.headers.Authorization = `Bearer ${newToken}`;
          return client.request(error.config);
        } catch {
          // 静默刷新失败，继续登出流程
        }
      }

      // 登出流程：通知组件缓存表单数据 → SessionExpiryModal 统一处理跳转（10s 倒计时）
      if (!isLoginRequest) {
        emitSessionExpired();
        safeRemoveItem("accessToken");
        // 通知 AuthContext 同步清除 token 状态，避免状态不同步导致闪屏
        emitForceLogout();
      }
    }
    return Promise.reject(error);
  },
);

// ── Auth ──
export const apiLogin = (body: { username: string; password: string }) => client.post("/auth/login", body);
export const apiGetMe = () => client.get("/auth/me");
// Phase 0 跨子域 SSO：凭共享 HttpOnly Cookie(rt) 静默换签 access JWT（必须带凭据）
export const apiRefresh = () => client.post("/auth/refresh", null, { withCredentials: true });

// ── Dashboard / KPI ──
export const apiDashboardKPI = (params?: Record<string, unknown>) => client.get("/kpi/dashboard", { params });
export const apiKPIQuery = (params: Record<string, unknown>) => client.get("/kpi/query", { params });

// ── Meta（行业演示 profile）──
export const apiMetaProfile = () => client.get("/meta/profile");
export const apiSwitchProfile = (profile: string) => client.post("/meta/switch-profile", { profile });

// ── ChatBI ──
export const apiChatBIQuery = (data: { query: string }, signal?: AbortSignal) =>
	client.post("/chatbi/query", data, { signal, timeout: 40_000 });

// ── IAM ──
// ── Feedback 问题反馈（BUG/建议 + 截图）──
export const apiCreateFeedback = (body: Record<string, unknown>) => client.post("/feedback", body);
export const apiListFeedbacks = (params?: Record<string, unknown>) => client.get("/feedback", { params });

// ── 前端错误监控上报 ──
export const apiReportClientErrors = (errors: Record<string, unknown>[]) =>
  client.post("/admin/client-errors", { errors });

// ── Web Vitals 性能监控 ──
export const apiReportVitals = (data: Record<string, unknown>) =>
  client.post("/admin/client-vitals", data);
export const apiGetVitalsStats = () =>
  client.get("/admin/client-vitals/stats");
// 错误查询（此前只有上报，没有查询 —— 监控页的错误 tab 才能做成真功能）
export const apiListClientErrors = (params?: Record<string, unknown>) =>
  client.get("/admin/client-errors", { params });
export const apiGetClientErrorStats = () =>
  client.get("/admin/client-errors/stats");

export const apiListUsers = (params?: Record<string, unknown>) => client.get("/iam/users", { params });
export const apiCreateUser = (body: Record<string, unknown>) => client.post("/iam/users", body);
export const apiUpdateUser = (id: string, body: Record<string, unknown>) => client.put(`/iam/users/${id}`, body);
export const apiListRoles = (params?: Record<string, unknown>) => client.get("/iam/roles", { params });
export const apiCreateRole = (body: Record<string, unknown>) => client.post("/iam/roles", body);
export const apiListCompanies = (params?: Record<string, unknown>) => client.get("/company/companies", { params });
export const apiCreateCompany = (body: Record<string, unknown>) => client.post("/company/companies", body);
// API Key management (MCP/WorkBuddy access)
export const apiListApiKeys = (params?: Record<string, unknown>) => client.get("/iam/api-keys", { params });
export const apiCreateApiKey = (body: Record<string, unknown>) => client.post("/iam/api-keys", body);
export const apiDeleteApiKey = (id: string) => client.delete(`/iam/api-keys/${id}`);

// ── Org 组织架构（树 + 成员 + 飞书同步）──
export const apiOrgTree = () => client.get("/org/tree");
export const apiOrgMembers = (nodeId?: string) => client.get("/org/members", { params: nodeId ? { node_id: nodeId } : undefined });
export const apiOrgSyncStatus = () => client.get("/org/sync/status");
export const apiOrgSync = () => client.post("/org/sync");
export const apiOrgBind = (openId: string) => client.post("/org/bind", { open_id: openId });
export const apiOrgBindAdmin = (openId: string, userId: string) => client.post("/org/bind/admin", { open_id: openId, user_id: userId });

// ── 系统设置：大模型 / OCR 配置（仅 admin）──
export const apiListAIConfigs = () => client.get("/admin/ai-configs");
export const apiCreateAIConfig = (body: Record<string, unknown>) => client.post("/admin/ai-configs", body);
export const apiUpdateAIConfig = (id: string, body: Record<string, unknown>) => client.put(`/admin/ai-configs/${id}`, body);
export const apiDeleteAIConfig = (id: string) => client.delete(`/admin/ai-configs/${id}`);
export const apiTestAIConfig = (body: Record<string, unknown>) => client.post("/admin/ai-configs/test", body, { timeout: 25_000 });
export const apiGetOCRConfig = () => client.get("/admin/ocr-config");
export const apiSaveOCRConfig = (body: Record<string, unknown>) => client.put("/admin/ocr-config", body);
/** OCR 连通性测试：保存前先验证凭证（密钥留空/掩码时后端用库里已存的） */
export const apiTestOCRConfig = (body: Record<string, unknown>) => client.post("/admin/ocr-config/test", body, { timeout: 25_000 });

// ── 飞书 OAuth2 单点登录 ──
// 跳转网关 → 302 到飞书授权页（浏览器整页跳转，不用 axios）
export const feishuLoginUrl = () => `${BASE_URL}/auth/feishu/login`;

// ── Inventory ──
export const apiListMaterials = (params?: Record<string, unknown>) => client.get("/inv/materials", { params });
export const apiCreateMaterial = (body: Record<string, unknown>) => client.post("/inv/materials", body);
export const apiUpdateMaterial = (id: string, body: Record<string, unknown>) => client.put(`/inv/materials/${id}`, body);
export const apiBatchGetStock = (materialIds: string[]) => client.get("/inv/stock/batch", { params: { material_id: materialIds } });
export const apiListMovements = (params?: Record<string, unknown>) => client.get("/inv/movements", { params });
export const apiCreateMovement = (body: Record<string, unknown>) => client.post("/inv/movements", body);
export const apiGetMovement = (id: string) => client.get(`/inv/movements/${id}`);
export const apiApproveMovement = (id: string, body: Record<string, unknown>) => client.post(`/inv/movements/${id}/approve`, body);
export const apiCheckAvailability = (params: Record<string, unknown>) => client.get("/inv/stock/check-availability", { params });
export const apiListWarehouses = (params?: Record<string, unknown>) => client.get("/inv/warehouses", { params });
export const apiListLocations = (params?: Record<string, unknown>) => client.get("/inv/locations", { params });
// Barcode
export const apiGetMaterialByBarcode = (barcode: string) => client.get(`/inv/materials/barcode/${encodeURIComponent(barcode)}`);
// OCR
export const apiOCROrder = (imageBase64: string) => client.post("/ocr/order", { image_base64: imageBase64 }, { timeout: 90_000 });
export const apiOCROrderText = (text: string) => client.post("/ocr/order", { text }, { timeout: 90_000 });

// ── Stock Check ──
export const apiListStockChecks = (params?: Record<string, unknown>) => client.get("/inv/stock-checks", { params });
export const apiCreateStockCheck = (body: Record<string, unknown>) => client.post("/inv/stock-checks", body);

// ── Purchase ──
export const apiListPOs = (params?: Record<string, unknown>) => client.get("/pur/orders", { params });
export const apiCreatePO = (body: Record<string, unknown>) => client.post("/pur/orders", body);
export const apiUpdatePOStatus = (id: string, body: Record<string, unknown>) => client.put(`/pur/orders/${id}/status`, body);
export const apiListReceipts = (params?: Record<string, unknown>) => client.get("/pur/receipts", { params });
export const apiListSuppliers = (params?: Record<string, unknown>) => client.get("/pur/suppliers", { params });
export const apiCreateSupplier = (body: Record<string, unknown>) => client.post("/pur/suppliers", body);
export const apiEvaluateSupplier = (id: string, body: Record<string, unknown>) => client.post(`/pur/suppliers/${id}/evaluate`, body);
export const apiAutoScoreSupplier = (id: string) => client.post(`/pur/suppliers/${id}/auto-score`, {});
export const apiListEvaluationHistory = (id: string, params?: Record<string, unknown>) => client.get(`/pur/suppliers/${id}/evaluations`, { params });
export const apiListQualifiedSuppliers = (params?: Record<string, unknown>) => client.get("/pur/suppliers/qualified", { params });
export const apiUpdateSupplierStatus = (id: string, body: Record<string, unknown>) => client.put(`/pur/suppliers/${id}/status`, body);
export const apiListSupplierPrices = (params?: Record<string, unknown>) => client.get("/pur/supplier-price", { params });
export const apiCreateSupplierPrice = (body: Record<string, unknown>) => client.post("/pur/supplier-price", body);

// ── Customer ──
export const apiListCustomers = (params?: Record<string, unknown>) => client.get("/customers", { params });
export const apiCreateCustomer = (body: Record<string, unknown>) => client.post("/customers", body);
export const apiGetCustomer = (id: string) => client.get(`/customers/${id}`);
export const apiUpdateCustomer = (id: string, body: Record<string, unknown>) => client.put(`/customers/${id}`, body);
export const apiDeleteCustomer = (id: string) => client.delete(`/customers/${id}`);

// ── Sales ──
export const apiListSalesOrders = (params?: Record<string, unknown>) => client.get("/sales/orders", { params });
export const apiCreateSalesOrder = (body: Record<string, unknown>) => client.post("/sales/orders", body);
export const apiGetSalesOrder = (id: string) => client.get(`/sales/orders/${id}`);
export const apiUpdateSalesOrderStatus = (id: string, body: { status: string }) => client.put(`/sales/orders/${id}/status`, body);
export const apiLinkSalesOrderBOM = (id: string, body: { bom_id: string }) => client.put(`/sales/orders/${id}/bom`, body);

// ── Sales CRM (bounded context) ──
export const apiSalesListLeads = (params?: Record<string, unknown>) => client.get("/sales/leads", { params });
export const apiSalesCreateLead = (body: Record<string, unknown>) => client.post("/sales/leads", body);
export const apiSalesUpdateLead = (id: string, body: Record<string, unknown>) => client.put(`/sales/leads/${id}`, body);
export const apiSalesQualifyLead = (id: string) => client.post(`/sales/leads/${id}/qualify`, {});
export const apiSalesRejectLead = (id: string) => client.post(`/sales/leads/${id}/reject`, {});
export const apiSalesConvertLead = (id: string, body: Record<string, unknown>) => client.post(`/sales/leads/${id}/convert`, body);
export const apiSalesListOpportunities = (params?: Record<string, unknown>) => client.get("/sales/opportunities", { params });
export const apiSalesCreateOpportunity = (body: Record<string, unknown>) => client.post("/sales/opportunities", body);
export const apiSalesUpdateOpportunity = (id: string, body: Record<string, unknown>) => client.put(`/sales/opportunities/${id}`, body);
export const apiSalesWinOpportunity = (id: string, body: Record<string, unknown>) => client.post(`/sales/opportunities/${id}/win`, body);
export const apiSalesLoseOpportunity = (id: string, body: Record<string, unknown>) => client.post(`/sales/opportunities/${id}/lose`, body);
export const apiSalesGetBoard = () => client.get("/sales/board");
export const apiSalesListStages = (params?: Record<string, unknown>) => client.get("/sales/pipeline/stages", { params });
export const apiSalesUpsertStage = (body: Record<string, unknown>) => client.post("/sales/pipeline/stages", body);
export const apiSalesListQuotes = (params?: Record<string, unknown>) => client.get("/sales/quotes", { params });
export const apiSalesCreateQuote = (body: Record<string, unknown>) => client.post("/sales/quotes", body);
export const apiSalesUpdateQuoteStatus = (id: string, body: { status: string }) => client.put(`/sales/quotes/${id}/status`, body);
export const apiSalesListContracts = (params?: Record<string, unknown>) => client.get("/sales/contracts", { params });
export const apiSalesCreateContract = (body: Record<string, unknown>) => client.post("/sales/contracts", body);
export const apiSalesSignContract = (id: string, body: Record<string, unknown>) => client.post(`/sales/contracts/${id}/sign`, body);
export const apiSalesListSnapshots = (params?: Record<string, unknown>) => client.get("/sales/order-snapshots", { params });

// ── HR 员工全在职周期管理 ──
export const apiHrListEmployees = (params?: Record<string, unknown>) => client.get("/hr/employees", { params });
export const apiHrUpsertEmployee = (body: Record<string, unknown>) => client.post("/hr/employees", body);
export const apiHrBatchImportEmployees = (body: Record<string, unknown>) => client.post("/hr/employees/batch-import", body);
export const apiHrGetEmployee = (id: string) => client.get(`/hr/employees/${id}`);
export const apiHrListEvents = (id: string, params?: Record<string, unknown>) => client.get(`/hr/employees/${id}/events`, { params });
export const apiHrAppendEvent = (id: string, body: Record<string, unknown>) => client.post(`/hr/employees/${id}/events`, body);
export const apiHrListContributions = (id: string, params?: Record<string, unknown>) => client.get(`/hr/employees/${id}/contributions`, { params });
export const apiHrRecordContribution = (body: Record<string, unknown>) => client.post("/hr/contributions", body);
export const apiHrListAssessments = (params?: Record<string, unknown>) => client.get("/hr/assessments", { params });
export const apiHrCreateAssessment = (body: Record<string, unknown>) => client.post("/hr/assessments", body);
export const apiHrGetAssessment = (id: string) => client.get(`/hr/assessments/${id}`);
export const apiHrUpdateAssessment = (id: string, body: Record<string, unknown>) => client.put(`/hr/assessments/${id}`, body);
export const apiHrListCompetency = (id: string) => client.get(`/hr/employees/${id}/competency`);
export const apiHrCreateCompetency = (id: string, body: Record<string, unknown>) => client.post(`/hr/employees/${id}/competency`, body);
export const apiHrGetTenantConfig = (tenantId: string) => client.get(`/hr/tenants/${tenantId}/config`);
export const apiHrUpsertTenantConfig = (tenantId: string, body: Record<string, unknown>) => client.put(`/hr/tenants/${tenantId}/config`, body);
export const apiHrGetBoard = (params?: Record<string, unknown>) => client.get("/hr/board", { params });

// ── HR M1 双通道任职资格引擎 ──
export const apiHrListQualDefs = (params?: Record<string, unknown>) => client.get("/hr/qualification-defs", { params });
export const apiHrUpsertQualDef = (body: Record<string, unknown>) => client.post("/hr/qualification-defs", body);
export const apiHrCheckQualification = (body: Record<string, unknown>) => client.post("/hr/qualification/check", body);
export const apiHrListPromotions = (params?: Record<string, unknown>) => client.get("/hr/promotions", { params });
export const apiHrCreatePromotion = (body: Record<string, unknown>) => client.post("/hr/promotions", body);
export const apiHrGetPromotion = (id: string) => client.get(`/hr/promotions/${id}`);
export const apiHrSubmitPromotion = (id: string, body?: Record<string, unknown>) => client.post(`/hr/promotions/${id}/submit`, body);
export const apiHrReviewPromotion = (id: string, body: Record<string, unknown>) => client.post(`/hr/promotions/${id}/review`, body);
export const apiHrVotePromotion = (id: string, body: Record<string, unknown>) => client.post(`/hr/promotions/${id}/vote`, body);
export const apiHrAnnouncePromotion = (id: string, body?: Record<string, unknown>) => client.post(`/hr/promotions/${id}/announce`, body);
export const apiHrAppointPromotion = (id: string, body?: Record<string, unknown>) => client.post(`/hr/promotions/${id}/appoint`, body);
export const apiHrListGates = (params?: Record<string, unknown>) => client.get("/hr/stage-gates", { params });
export const apiHrOpenStageGate = (body: Record<string, unknown>) => client.post("/hr/stage-gates", body);
export const apiHrReviewStageGate = (id: string, body: Record<string, unknown>) => client.post(`/hr/stage-gates/${id}/review`, body);

// ── HR M2 停滞/风险预警 + 继任规划 ──
export const apiHrRunRiskScan = (params?: Record<string, unknown>) => client.post("/hr/risk/scan", null, { params });
export const apiHrListRiskAlerts = (params?: Record<string, unknown>) => client.get("/hr/risk/alerts", { params });
export const apiHrGetRiskAlert = (id: string) => client.get(`/hr/risk/alerts/${id}`);
export const apiHrResolveRiskAlert = (id: string, body: Record<string, unknown>) => client.post(`/hr/risk/alerts/${id}/resolve`, body);
export const apiHrListSuccessionPlans = (params?: Record<string, unknown>) => client.get("/hr/succession-plans", { params });
export const apiHrUpsertSuccessionPlan = (body: Record<string, unknown>) => client.post("/hr/succession-plans", body);
export const apiHrGetSuccessionPlan = (id: string) => client.get(`/hr/succession-plans/${id}`);
export const apiHrEvaluateSuccession = (id: string) => client.post(`/hr/succession-plans/${id}/evaluate`);

// ── HR 知识留存（Knowledge Transfer）──
export const apiHrCreateKnowledgeTransfer = (body: Record<string, unknown>) => client.post("/hr/knowledge-transfer", body);
export const apiHrListKnowledgeTransfers = (params?: Record<string, unknown>) => client.get("/hr/knowledge-transfer", { params });
export const apiHrGetKnowledgeTransfer = (id: string) => client.get(`/hr/knowledge-transfer/${id}`);
export const apiHrUpdateKTStatus = (id: string, body: Record<string, unknown>) => client.put(`/hr/knowledge-transfer/${id}/status`, body);
export const apiHrUpdateKTProgress = (id: string, progress: number) => client.put(`/hr/knowledge-transfer/${id}/progress`, { progress });

// ── HR AI 排名 ──
export const apiHrAIEvaluate = (empId: string) => client.post("/hr/ai-ranking/evaluate", { empId });
export const apiHrAIBatchRanking = (empIds: string[]) => client.post("/hr/ai-ranking/batch", { empIds });

// ── HR M2 动态档案（周期访谈/培训/日报/阶段汇报/调薪/管理链）──
export const apiHrCreateInterviewRecord = (body: Record<string, unknown>) => client.post("/hr/interview-records", body);
export const apiHrListInterviewRecords = (params?: Record<string, unknown>) => client.get("/hr/interview-records", { params });
export const apiHrCreateTrainingRecord = (body: Record<string, unknown>) => client.post("/hr/training-records", body);
export const apiHrListTrainingRecords = (params?: Record<string, unknown>) => client.get("/hr/training-records", { params });
export const apiHrCreateDailyWorkLog = (body: Record<string, unknown>) => client.post("/hr/daily-work-logs", body);
export const apiHrListDailyWorkLogs = (params?: Record<string, unknown>) => client.get("/hr/daily-work-logs", { params });
export const apiHrCreateStageReport = (body: Record<string, unknown>) => client.post("/hr/stage-reports", body);
export const apiHrListStageReports = (params?: Record<string, unknown>) => client.get("/hr/stage-reports", { params });
export const apiHrCreateCompensationRecord = (body: Record<string, unknown>) => client.post("/hr/compensation-records", body);
export const apiHrListCompensationRecords = (params?: Record<string, unknown>) => client.get("/hr/compensation-records", { params });
export const apiHrGetManagementChain = (params?: Record<string, unknown>) => client.get("/hr/management-chain", { params });

// ── HR 租户配置检查 ──
export const apiHrValidateTenantConfig = (tenantId: string) => client.get(`/hr/tenant-config/${tenantId}/validate`);

// ── PM 项目管理 ──
export const apiProjectList = (params?: Record<string, unknown>) => client.get("/project/projects", { params });
export const apiProjectCreate = (body: Record<string, unknown>) => client.post("/project/projects", body);
export const apiProjectGet = (id: string) => client.get(`/project/projects/${id}`);
export const apiProjectUpdate = (id: string, body: Record<string, unknown>) => client.put(`/project/projects/${id}`, body);
export const apiProjectTransition = (id: string, action: string) => client.post(`/project/projects/${id}/transition`, { action });
export const apiProjectAccept = (id: string) => client.post(`/project/projects/${id}/accept`, {});
export const apiProjectListTemplates = (params?: Record<string, unknown>) => client.get("/project/templates", { params });
export const apiProjectUpsertTemplate = (body: Record<string, unknown>) => client.post("/project/templates", body);
export const apiProjectListMilestones = (projectId: string, params?: Record<string, unknown>) => client.get(`/project/projects/${projectId}/milestones`, { params });
export const apiProjectUpdateMilestone = (id: string, body: Record<string, unknown>) => client.put(`/project/milestones/${id}`, body);
export const apiProjectConfirmMilestone = (id: string) => client.post(`/project/milestones/${id}/confirm`, {});
export const apiProjectListTasks = (projectId: string, params?: Record<string, unknown>) => client.get(`/project/projects/${projectId}/tasks`, { params });
export const apiProjectCreateTask = (body: Record<string, unknown>) => client.post("/project/tasks", body);
export const apiProjectUpdateTask = (id: string, body: Record<string, unknown>) => client.put(`/project/tasks/${id}`, body);
export const apiProjectTransitionTask = (id: string, action: string) => client.post(`/project/tasks/${id}/transition`, { action });
export const apiProjectListReports = (projectId: string, params?: Record<string, unknown>) => client.get(`/project/projects/${projectId}/reports`, { params });
export const apiProjectSubmitReport = (body: Record<string, unknown>) => client.post("/project/reports", body);
export const apiProjectDelayScan = () => client.post("/project/delay/scan", {});
export const apiProjectBoard = () => client.get("/project/board");
export const apiProjectListFieldDefs = (params?: Record<string, unknown>) => client.get("/project/custom-fields", { params });
export const apiProjectUpsertFieldDef = (body: Record<string, unknown>) => client.post("/project/custom-fields", body);

// ── Workspace 可配置角色工作区（O10 敏态层）──
export const apiWorkspaceGet = (params?: Record<string, unknown>) => client.get("/workspace", { params });
export const apiWorkspaceListRoles = () => client.get("/workspace/roles");
export const apiWorkspaceListTemplates = () => client.get("/workspace/templates");
export const apiWorkspaceSaveTemplate = (body: Record<string, unknown>) => client.post("/workspace/templates", body);
export const apiWorkspaceGetOverride = (params?: Record<string, unknown>) => client.get("/workspace/overrides", { params });
export const apiWorkspaceSaveOverride = (body: Record<string, unknown>) => client.post("/workspace/overrides", body);
export const apiWorkspaceGetBadges = (params?: Record<string, unknown>) => client.get("/workspace/badges", { params });

// ── Production ──
export const apiListBOMs = (params?: Record<string, unknown>) => client.get("/prp/boms", { params });
export const apiCreateBOM = (body: Record<string, unknown>) => client.post("/prp/boms", body);
export const apiGetBOM = (id: string) => client.get(`/prp/boms/${id}`);
export const apiUpdateBOM = (id: string, body: Record<string, unknown>) => client.put(`/prp/boms/${id}`, body);
export const apiDeleteBOM = (id: string) => client.delete(`/prp/boms/${id}`);
export const apiListWorkOrders = (params?: Record<string, unknown>) => client.get("/prp/workorders", { params });
export const apiCreateWorkOrder = (body: Record<string, unknown>) => client.post("/prp/workorders", body);
export const apiUpdateWOStatus = (id: string, body: Record<string, unknown>) => client.put(`/prp/workorders/${id}/status`, body);
export const apiGenerateSchedule = (body: Record<string, unknown>) => client.post("/prp/schedule/generate", body);

// ── Workshop ──
export const apiListOperations = (params?: Record<string, unknown>) => client.get("/mes/operations", { params });
export const apiCreateOperation = (body: Record<string, unknown>) => client.post("/mes/operations", body);
export const apiUpdateOperationStatus = (id: string, body: Record<string, unknown>) => client.put(`/mes/operations/${id}/status`, body);
export const apiListProductionReports = (params?: Record<string, unknown>) => client.get("/mes/reports", { params });
export const apiCreateProductionReport = (body: Record<string, unknown>) => client.post("/mes/reports", body);
export const apiGetWIPStatus = (workOrderId: string) => client.get("/mes/wip", { params: { work_order_id: workOrderId } });

// ── Equipment ──
export const apiListEquipment = (params?: Record<string, unknown>) => client.get("/eqp/equipment", { params });
export const apiCreateEquipment = (body: Record<string, unknown>) => client.post("/eqp/equipment", body);
export const apiUpdateEquipment = (id: string, body: Record<string, unknown>) => client.put(`/eqp/equipment/${id}`, body);
export const apiListDataPoints = (params?: Record<string, unknown>) => client.get("/eqp/datapoints", { params });
export const apiQueryReadings = (params: Record<string, unknown>) => client.get("/eqp/readings", { params });

// ── Quality ──
export const apiListInspections = (params?: Record<string, unknown>) => client.get("/qms/inspections", { params });
export const apiCreateInspection = (body: Record<string, unknown>) => client.post("/qms/inspections", body);
export const apiRecordInspectionResult = (id: string, body: Record<string, unknown>) => client.put(`/qms/inspections/${id}/result`, body);
export const apiListDefects = (params?: Record<string, unknown>) => client.get("/qms/defects", { params });
export const apiCreateDefect = (body: Record<string, unknown>) => client.post("/qms/defects", body);
export const apiUpdateDefectDisposition = (id: string, body: Record<string, unknown>) => client.put(`/qms/defects/${id}/disposition`, body);

// ── Traceability ──
export const apiListBatches = (params?: Record<string, unknown>) => client.get("/trace/batches", { params });
export const apiGetBatch = (id: string) => client.get(`/trace/batches/${id}`);
export const apiGetSerialNumber = (id: string) => client.get(`/trace/serial-numbers/${id}`);
export const apiTraceForward = (body: Record<string, unknown>) => client.post("/trace/trace-forward", body);
export const apiTraceBackward = (body: Record<string, unknown>) => client.post("/trace/trace-backward", body);

// ── Finance ──
export const apiListAccounts = (params?: Record<string, unknown>) => client.get("/fin/accounts", { params });
export const apiCreateAccount = (body: Record<string, unknown>) => client.post("/fin/accounts", body);
export const apiUpdateAccount = (id: string, body: Record<string, unknown>) => client.put(`/fin/accounts/${id}`, body);
export const apiListJournals = (params?: Record<string, unknown>) => client.get("/fin/journals", { params });
export const apiCreateJournal = (body: Record<string, unknown>) => client.post("/fin/journals", body);
export const apiUpdateJournal = (id: string, body: Record<string, unknown>) => client.put(`/fin/journals/${id}`, body);
export const apiListInvoices = (params?: Record<string, unknown>) => client.get("/fin/invoices", { params });
export const apiCreateInvoice = (body: Record<string, unknown>) => client.post("/fin/invoices", body);
export const apiUpdateInvoice = (id: string, body: Record<string, unknown>) => client.put(`/fin/invoices/${id}`, body);
export const apiListCostCenters = (params?: Record<string, unknown>) => client.get("/fin/cost-centers", { params });
export const apiCreateCostCenter = (body: Record<string, unknown>) => client.post("/fin/cost-centers", body);
export const apiUpdateCostCenter = (id: string, body: Record<string, unknown>) => client.put(`/fin/cost-centers/${id}`, body);
export const apiCalculateCost = (body: Record<string, unknown>) => client.post("/fin/cost-centers/calculate", body);

// ── Approval ──
export const apiListApprovalRecords = (params?: Record<string, unknown>) => client.get("/approval/records", { params });
export const apiListApprovalDefinitions = (params?: Record<string, unknown>) => client.get("/approval/definitions", { params });
export const apiCreateApprovalDefinition = (body: Record<string, unknown>) => client.post("/approval/definitions", body);
export const apiUpdateApprovalDefinition = (id: string, body: Record<string, unknown>) => client.put(`/approval/definitions/${id}`, body);
export const apiDeleteApprovalDefinition = (id: string) => client.delete(`/approval/definitions/${id}`);
// 条件规则字段下拉：按 biz_type 返回可条件化的字段列表
export const apiBizFields = (bizType: string) => client.get("/approval/biz-fields", { params: { biz_type: bizType } });
export const apiDecideApproval = (id: string, body: Record<string, unknown>) => client.post(`/approval/records/${id}/decide`, body);

// ── Reminder ──
export const apiListReminderRules = (params?: Record<string, unknown>) => client.get("/reminder/rules", { params });
export const apiCreateReminderRule = (body: Record<string, unknown>) => client.post("/reminder/rules", body);
export const apiUpdateReminderRule = (id: string, body: Record<string, unknown>) => client.put(`/reminder/rules/${id}`, body);
export const apiDeleteReminderRule = (id: string) => client.delete(`/reminder/rules/${id}`);
export const apiTriggerReminderScan = () => client.post("/reminder/scan");

// ── Notification (Phase 3) ──
export const apiListChannels = () => client.get("/notifications/channels");
export const apiSendNotification = (body: Record<string, unknown>) => client.post("/notifications/send", body);

// ── 全局消息中心（持久化收件箱）──
export const apiListInbox = (params?: Record<string, unknown>) => client.get("/notifications/inbox", { params });
export const apiMarkInboxRead = (body: Record<string, unknown>) => client.put("/notifications/inbox/read", body);
export const apiUnreadCount = () => client.get("/notifications/unread-count");
export const apiGetSubscriptions = () => client.get("/notifications/subscriptions");
export const apiUpdateSubscriptions = (body: Record<string, unknown>) => client.put("/notifications/subscriptions", body);

// ── SOP ──
// D10: 列表统一用语义化路径 GET /sop/documents（旧 /sop/documents-list 仅后端兼容保留）
export const apiListSOPs = (params?: Record<string, unknown>) => client.get("/sop/documents", { params });
export const apiCreateSOP = (body: Record<string, unknown>) => client.post("/sop/documents", body);
export const apiGetSOP = (id: string) => client.get(`/sop/documents/${id}`);
export const apiAddSOPStep = (id: string, body: Record<string, unknown>) => client.post(`/sop/documents/${id}/steps`, body);
export const apiUpdateSOPStep = (id: string, body: Record<string, unknown>) => client.put(`/sop/documents/${id}/steps`, body);
export const apiCreateSOPVersion = (id: string, body: Record<string, unknown>) => client.post(`/sop/documents/${id}/versions`, body);
export const apiListSOPVersions = (id: string) => client.get(`/sop/documents/${id}/versions`);

// ── Andon ──
export const apiListAndonAlerts = (params?: Record<string, unknown>) => client.get("/andon/alerts", { params });
export const apiCreateAndonAlert = (body: Record<string, unknown>) => client.post("/andon/alerts", body);
export const apiGetAndonAlert = (id: string) => client.get(`/andon/alerts/${id}`);
export const apiRespondAndonAlert = (id: string, body: Record<string, unknown>) => client.post(`/andon/alerts/${id}/respond`, body);
export const apiEscalateAndonAlert = (id: string, body: Record<string, unknown>) => client.post(`/andon/alerts/${id}/escalate`, body);
export const apiGetAndonHistory = () => client.get("/andon/history");
export const apiStopLine = (body: Record<string, unknown>) => client.post("/andon/stop-line", body);
export const apiResumeLine = (body: Record<string, unknown>) => client.post("/andon/resume-line", body);

// ── MatVerify (上料防错) ──
export const apiVerifyMaterial = (body: Record<string, unknown>) => client.post("/matvfy/verify", body);
export const apiBypassVerification = (body: Record<string, unknown>) => client.post("/matvfy/bypass", body);
export const apiGetVerificationLog = (params?: Record<string, unknown>) => client.get("/matvfy/logs", { params });

// ── Purchase Requisition ──
export const apiListRequisitions = (params?: Record<string, unknown>) => client.get("/purchase-requisition/requisitions", { params });
export const apiCreateRequisition = (body: Record<string, unknown>) => client.post("/purchase-requisition/requisitions", body);
// 闭环（批次六十一）：提交 → 批准/驳回 → 转采购单。
// 路径是「动作在前、单据 id 在请求体」—— 网关权限按字面前缀匹配，只有这样三个动作才能分别控权。
export const apiSubmitRequisition = (id: string) => client.post("/purchase-requisition/submit", { id });
export const apiApproveRequisition = (body: { id: string; approved: boolean; comment?: string }) =>
  client.post("/purchase-requisition/approve", body);
export const apiConvertRequisition = (body: { id: string; supplierId: string }) => client.post("/purchase-requisition/convert", body);

// ── Pricing ──
export const apiListPricings = (params?: Record<string, unknown>) => client.get("/pricing/quotes", { params });
export const apiCreatePricing = (body: Record<string, unknown>) => client.post("/pricing/quotes", body);
export const apiListPriceLists = (params?: Record<string, unknown>) => client.get("/pricing/lists", { params });

// ── AI: AI小原 智能管家 ──
// signal 用于「停止生成」按钮中止请求（AbortController）
export const apiAITChat = (body: { query: string; session_id?: string }, signal?: AbortSignal) =>
	client.post("/ait/chat", body, { signal, timeout: 60_000 });

// SSE 流式产物：文本块或结构化工具结果。
export interface AITStreamText {
	type: "text";
	text: string;
}
export interface AITStreamTool {
	type: "tool_result";
	tool: string;
	success: boolean;
	isError: boolean;
	lines: string[];
	note?: string;
	args?: Record<string, unknown>;
	needsConfirm?: boolean;
}
export type AITStreamChunk = AITStreamText | AITStreamTool;

// apiAITChatStream returns an async generator that yields SSE chunks
// (text blocks + structured tool_result events for ToolExecCard).
export async function* apiAITChatStream(body: { query: string; session_id?: string; page_name?: string; context?: Record<string, string> }, signal?: AbortSignal) {
	const token = localStorage.getItem("accessToken");
	const headers: Record<string, string> = { "Content-Type": "application/json" };
	if (token) headers["Authorization"] = `Bearer ${token}`;

	const resp = await fetch(`${BASE_URL}/ait/chat`, {
		method: "POST",
		headers,
		body: JSON.stringify({ ...body, stream: true }),
		signal,
	});

	if (!resp.ok) {
		const errText = await resp.text().catch(() => "unknown error");
		throw new Error(`AI 服务返回错误 (${resp.status}): ${errText}`);
	}

	const reader = resp.body?.getReader();
	if (!reader) throw new Error("SSE reader unavailable");

	const decoder = new TextDecoder();
	let buf = "";
	while (true) {
		const { done, value } = await reader.read();
		if (done) break;
		buf += decoder.decode(value, { stream: true });
		// SSE 事件以 \n\n 分隔；按完整事件解析，避免高负载 JSON 被 TCP 帧截断
		const events = buf.split("\n\n");
		buf = events.pop() || "";
		for (const evt of events) {
			if (!evt.trim()) continue;
			const lines = evt.split("\n");
			let eventType = "";
			let dataStr = "";
			for (const line of lines) {
				const trimmed = line.trim();
				if (trimmed.startsWith("event: ")) {
					eventType = trimmed.slice(7);
				} else if (trimmed.startsWith("data: ")) {
					dataStr = trimmed.slice(6);
				}
			}
			if (!dataStr) continue;
			if (dataStr === "[DONE]") return;
			if (eventType === "done") continue;
			if (eventType === "error") {
				throw new Error(dataStr.replace(/\\n/g, "\n"));
			}
			if (eventType === "tool_result") {
				let parsed: any;
				try {
					parsed = JSON.parse(dataStr);
				} catch {
					parsed = null;
				}
				if (parsed && parsed.tool) {
					yield {
						type: "tool_result",
						tool: parsed.tool,
						success: parsed.success,
						isError: parsed.isError,
						lines: parsed.lines || [],
						note: parsed.note,
						args: parsed.args,
						needsConfirm: parsed.needs_confirm || false,
					} as AITStreamTool;
				}
			} else {
				// 文本块：data 是 JSON {"text":"...","session_id":"..."}
				let text: string;
				try {
					const obj = JSON.parse(dataStr);
					text = obj.text || "";
				} catch {
					text = dataStr.replace(/\\n/g, "\n");
				}
				if (text) yield { type: "text", text } as AITStreamText;
			}
		}
	}
}

export const apiAITAnalyze = (body: { query: string; context?: string }) => client.post("/ait/analyze", body, { timeout: 120_000 });
export const apiAITSuggest = (body: { context: string }) => client.post("/ait/suggest", body);

// ── AI: BomCoder ──
// 注意：后端 proto json tag 为 snake_case（rule_set_id / full_code），必须显式映射，
// 否则 GenerateCode 收不到 ruleSetId → 固定落默认机械件规则；ValidateCode 收不到 code → 任何编码都误报"缺少必填段"
export const apiBomGenerateCode = (body: { description: string; category?: string; ruleSetId?: string }) =>
  client.post("/bom-coder/generate-code", {
    description: body.description,
    category: body.category,
    rule_set_id: body.ruleSetId,
  });
export const apiBomValidateCode = (body: { code: string; ruleSetId?: string }) =>
  client.post("/bom-coder/validate-code", {
    full_code: body.code,
    rule_set_id: body.ruleSetId,
  });
// 语义查重（防「一物多码」）：网关已把 matches 显式展开成数组（无重复时也是 []，不是 {}）
export const apiBomCheckDuplicate = (body: { material_name: string; spec?: string; category?: string; threshold?: number }) =>
  client.post("/bom-coder/check-duplicate", body);
export const apiBomListRules = (params?: Record<string, unknown>) => client.get("/bom-coder/rules", { params });
export const apiBomGetRule = (id: string) => client.get(`/bom-coder/rules/${id}`);
export const apiBomCreateRule = (body: Record<string, unknown>) => client.post("/bom-coder/rules", body);
export const apiBomUpdateRule = (id: string, body: Record<string, unknown>) => client.put(`/bom-coder/rules/${id}`, body);
export const apiBomDeleteRule = (id: string) => client.delete(`/bom-coder/rules/${id}`);
export const apiSuggestRuleUpdate = (params?: Record<string, unknown>) => client.get("/bom-coder/suggest-rules", { params });

// ── 全局搜索 ──
export interface SearchResultItem {
  type: "material" | "sales_order" | "employee" | "customer" | "work_order";
  id: string;
  name: string;
  sub: string;
  url: string;
}
export const apiGlobalSearch = (q: string, limit = 20) =>
  client.get("/search/", { params: { q, limit } }).then((r) => r.data.items as SearchResultItem[]);

// ── Storage (COS 对象存储) ──
export const apiStorageUpload = (formData: FormData, signal?: AbortSignal) =>
  client.post("/storage/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    signal,
    timeout: 60_000,
  });
export const apiStorageDownload = (key: string) => client.get(`/storage/files/${encodeURIComponent(key)}`);
export const apiStorageDelete = (key: string) => client.delete(`/storage/files/${encodeURIComponent(key)}`);
export const apiStorageList = (prefix?: string) => client.get("/storage/files", { params: prefix ? { prefix } : undefined });

// Redis key-value APIs
export const apiRedisPing = () => client.get("/redis/ping");
export const apiRedisGet = (key: string) => client.get(`/redis/${encodeURIComponent(key)}`);
export const apiRedisSet = (key: string, value: string, ttl?: number) =>
  client.post(`/redis/${encodeURIComponent(key)}${ttl ? `?ttl=${ttl}` : ""}`, value, {
    headers: { "Content-Type": "text/plain" },
  });
export const apiRedisDel = (key: string) => client.delete(`/redis/${encodeURIComponent(key)}`);

export default client;
